import { ReactNode, useEffect } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { Field } from "@atlaskit/form";
import TextField from "@atlaskit/textfield";
import { G300, N0, N40, N100, N200, R400 } from "@atlaskit/theme/colors";

import { useSiteNameValidation, ErrorType } from "@/components/signup/hooks";
import { isProduction, fontSize } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

import { useRecommendedSiteName } from "../../hooks/recommended-site-name";
import { useSiteActivationContext } from "../../hooks/site-activation-context";
import { messages as validationMessages } from "../../messages";

import { IsValidOrLoading } from "./is-valid-or-loading";

type InputSiteNameProps = {
    inputLabel?: ReactNode;
};

export const SiteNameInput = ({ inputLabel }: InputSiteNameProps) => {
    useRecommendedSiteName();

    const {
        setSiteNameValid,
        siteName,
        setSiteNameValue,
        isSubmitting,
        validationMessageState: [validationMessage, setValidationMessage]
    } = useSiteActivationContext();

    const { isValid, isValidating, error } = useSiteNameValidation(
        siteName,
        setSiteNameValid
    );

    const siteNameAddressSuffix = isProduction()
        ? ".atlassian.net"
        : ".jira-dev.com";

    useEffect(() => {
        switch (error) {
            case ErrorType.NO_ERROR:
                setValidationMessage(undefined);
                break;
            case ErrorType.INVALID_NAME:
                setValidationMessage(validationMessages.invalidName);
                break;
            case ErrorType.MAXIMUM_LENGTH:
                setValidationMessage(validationMessages.maxLength);
                break;
            case ErrorType.UNAVAILABLE:
                setValidationMessage(validationMessages.unavailable);
                break;
            case ErrorType.NETWORK_ERROR:
                setValidationMessage(validationMessages.networkError);
                break;
        }
    }, [error, setValidationMessage]);

    const label = inputLabel ?? (
        <FormattedMessage
            id="BxpUiSignup.nameSite.inputSiteNameLabel2"
            defaultMessage="Your site"
        />
    );

    // adding updated labels and submiting for translation using the old label till the new trasnlations are back
    //@ts-ignore
    const newLabel = (
        <FormattedMessage
            id="accountActivation.nameSite.inputSiteNameLabel"
            defaultMessage="Your site"
        />
    );

    return (
        <div data-testid="adg4-site-name-input" css={inputAndLabelStyles}>
            <div css={fieldWrapperCss}>
                <Field
                    aria-required={true}
                    isRequired
                    isDisabled={isSubmitting}
                    name="site-name"
                    label={<span css={inputLabelWrapperCss}>{label}</span>}
                >
                    {({ fieldProps }) => (
                        <div
                            css={[
                                siteName === "" && inputEmptyCss,
                                isValid && inputValidCss,
                                validationMessage !== undefined &&
                                    inputInvalidCss
                            ]}
                        >
                            <TextField
                                type="text"
                                autoComplete="on"
                                data-testId="site-name-input"
                                {...fieldProps}
                                value={siteName}
                                onChange={(e) => {
                                    setSiteNameValue(e.currentTarget.value);
                                    fieldProps.onChange(e);
                                }}
                                elemAfterInput={
                                    <div css={siteNameAfterCss}>
                                        <span css={siteNameAfterLabelCss}>
                                            {siteNameAddressSuffix}
                                        </span>
                                        <IsValidOrLoading
                                            isValidating={isValidating}
                                            siteNameValue={siteName}
                                            validationMessage={
                                                validationMessage
                                            }
                                        />
                                    </div>
                                }
                            />
                            {validationMessage && (
                                <div css={siteNameErrorCss}>
                                    <FormattedMessage {...validationMessage} />
                                </div>
                            )}
                        </div>
                    )}
                </Field>
            </div>
        </div>
    );
};

const inputEmptyCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["> div"]: {
        borderColor: `${N40} !important`
    }
});

const inputInvalidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["> div"]: {
        borderColor: `${R400} !important`
    }
});

const inputValidCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["> div"]: {
        borderColor: `${G300} !important`
    }
});

const fieldWrapperCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to style atlaskit input
    ["input"]: {
        backgroundColor: `${N0} !important`,
        flex: 1,
        fontSize: "1rem",
        width: "auto"
    },
    ["&:first-of-type"]: {
        marginBlockStart: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- needed to remove atlaskit required asterisk
    ["label span:nth-of-type(2)"]: {
        display: "none"
    }
});

const inputLabelWrapperCss = css({
    color: N200,
    display: "block",
    fontSize: "0.75rem",
    fontWeight: 500
});

const siteNameAfterLabelCss = css({
    backgroundColor: N0,
    fontSize: fontSize.sm,
    paddingInlineEnd: 8,
    whiteSpace: "nowrap"
});

const siteNameAfterCss = css({
    alignItems: "center",
    color: N100,
    display: "flex",
    fontSize: "1rem",
    fontWeight: 400,
    justifyContent: "flex-end",
    paddingInlineEnd: 8,
    width: "auto"
});

const siteNameErrorCss = css({
    color: R400,
    fontSize: "0.75rem",
    marginBlockStart: 6
});

const inputAndLabelStyles = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label + div > div:first-of-type, div[data-disabled]"]: {
        borderRadius: "21px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input, label + div > div > div"]: {
        paddingInlineStart: "12px !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["label > span"]: {
        color: wacToken("color.primary.black"),
        fontFamily: wacToken("font.charlie.text"),
        fontSize: "14px",
        fontWeight: "400",
        lineHeight: "20px"
    }
});
